import * as React from "react"
import { Check, Hash } from "lucide-react"
import { HexColorPicker } from "react-colorful"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { PALETTE } from "@/lib/colores-de-etiqueta"

/**
 * Elegir un color: diez de la paleta, o uno cualquiera.
 *
 * Vivia dentro de TagsManager, la unica pantalla que lo usa hoy — y esa es
 * justo la razon de sacarlo: la segunda pantalla que necesite elegir un color
 * lo volveria a escribir, y entonces habria dos paletas distintas.
 *
 * Sus `<button>` crudos se quedan aqui, que es donde estan bien. Una muestra de
 * color no es el componente `Button` -lo unico que tiene que ensenar es el
 * color, y `Button` traeria su fondo, su borde y su altura-, pero SI es un
 * boton: se pulsa y responde al teclado. Asi que se escribe como tal una vez,
 * en la pieza compartida, en vez de diez veces en cada vista.
 */

const colorPickerStyles = `
.tag-color-picker .react-colorful {
  width: 100%;
  height: 160px;
  border-radius: 12px;
  gap: 10px;
}
.tag-color-picker .react-colorful__saturation {
  border-radius: 10px;
  border-bottom: none;
}
.tag-color-picker .react-colorful__hue {
  height: 12px;
  border-radius: 6px;
}
.tag-color-picker .react-colorful__pointer {
  width: 18px;
  height: 18px;
  border-width: 2.5px;
}
`

export function ColorPicker({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const isMobile = useIsMobile()
  const [open, setOpen] = React.useState(false)
  const [hexInput, setHexInput] = React.useState(value)
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const isCustom = !PALETTE.some(c => c.hex.toLowerCase() === value.toLowerCase())

  React.useEffect(() => { setHexInput(value) }, [value])

  React.useEffect(() => {
    if (!open) return
    const handler = (e: PointerEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [open])

  const handleHexInput = (raw: string) => {
    const v = raw.startsWith('#') ? raw : `#${raw}`
    setHexInput(v)
    if (/^#[0-9A-Fa-f]{6}$/.test(v)) onChange(v)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {PALETTE.map(c => (
        <button
          key={c.hex}
          type="button"
          onClick={() => { onChange(c.hex); setOpen(false) }}
          className={cn(
            'h-7 w-7 rounded-full transition-all flex items-center justify-center shrink-0',
            value.toLowerCase() === c.hex.toLowerCase()
              ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110'
              : 'hover:scale-110'
          )}
          style={{ backgroundColor: c.hex }}
          title={c.label}
        >
          {value.toLowerCase() === c.hex.toLowerCase() && <Check className="h-3.5 w-3.5 text-white" />}
        </button>
      ))}

      {/* Rainbow gradient circle → custom picker */}
      <div className="relative shrink-0" ref={popoverRef}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className={cn(
            'h-7 w-7 rounded-full transition-all flex items-center justify-center',
            isCustom
              ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110'
              : 'hover:scale-110'
          )}
          style={{
            background: isCustom
              ? value
              : // eslint-disable-next-line no-restricted-syntax -- el arcoiris del selector es dato, no estilo
                'conic-gradient(#EF4444, #F59E0B, #84CC16, #10B981, #06B6D4, #1238c4, #8B5CF6, #EC4899, #EF4444)',
          }}
          title="Custom color"
        >
          {isCustom && <Check className="h-3.5 w-3.5 text-white drop-shadow-sm" />}
        </button>

        {open && (
          <div
            className={cn(
              "tag-color-picker z-50 rounded-lg border bg-background p-3 shadow-md animate-in fade-in zoom-in-95 duration-150",
              isMobile
                ? "fixed left-4 right-4 top-24 max-h-[70vh] overflow-auto"
                : "absolute right-0 top-10 w-56"
            )}
          >
            <style>{colorPickerStyles}</style>
            <HexColorPicker color={value} onChange={onChange} />
            <div className="mt-3 flex items-center gap-2">
              <div className="h-8 w-8 shrink-0 rounded-lg border" style={{ backgroundColor: value }} />
              <div className="relative flex-1">
                <Hash className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground/40" />
                <input
                  type="text"
                  value={hexInput.replace('#', '').toUpperCase()}
                  onChange={e => handleHexInput(e.target.value)}
                  maxLength={7}
                  className="h-8 w-full rounded-lg border bg-muted/30 pl-7 pr-2 text-xs font-mono uppercase tracking-wider text-foreground outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
                  placeholder="3B82F6"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
